/** 扫描凭证的双版本入口：原件列表与扫描PDF均可查看/下载，不覆盖或转换原件。
 * generic文件沿服务端绑定后的业务权限读取；私有附件调用方可提供自己的受控URL。
 */
import React, { useEffect, useState } from "react";
import { Files, Download, Loader2, FileText } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { scanSize } from "../../lib/documentScanner";

export default function DocumentScanVersions({ api, zh = true, fileId, versions, compact = true }) {
  const [open, setOpen] = useState(false), [items, setItems] = useState([]), [selected, setSelected] = useState(0);
  const [busy, setBusy] = useState(false), [error, setError] = useState("");
  const [preview, setPreview] = useState({ url: "", type: "" });
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    setBusy(true); setError(""); setItems([]); setSelected(0);
    const request = versions ? Promise.resolve({ data: { originals: versions } })
      : api.get(`/files/${encodeURIComponent(fileId)}/document-scan`, { signal: controller.signal });
    request.then(({ data }) => {
      if (controller.signal.aborted) return;
      const source = data.documentScan || data;
      setItems([...(source.scan ? [{ ...source.scan, scanned: true }] : []), ...(source.originals || [])]);
    }).catch(e => { if (!controller.signal.aborted) setError(e?.response?.data?.detail || (zh ? "版本加载失败，请重试。" : "Could not load versions. Retry.")); })
      .finally(() => { if (!controller.signal.aborted) setBusy(false); });
    return () => controller.abort();
  }, [api, fileId, versions, open, zh]);
  const current = items[selected];
  useEffect(() => {
    setPreview({ url: "", type: "" });
    if (!open || !current) return undefined;
    const controller = new AbortController(); let url = "";
    const type = current.contentType || "";
    const heicPreview = !current.url && /^image\/(heic|heif)$/.test(type);
    if (!heicPreview && !/^(image\/(jpeg|png|webp)|application\/pdf)$/.test(type)) return undefined;
    api.get(current.url || `/files/${encodeURIComponent(current.fileId)}/download`, {
      // 仅预览转JPEG；下载仍取原件字节。私有运营附件沿自己的受控URL读取。
      params: current.url ? undefined : { orig: 1, ...(heicPreview ? { fmt: "jpeg" } : {}) }, responseType: "blob", signal: controller.signal,
    }).then(({ data }) => {
      if (controller.signal.aborted) return;
      if (!/^(image\/(jpeg|png|webp)|application\/pdf)$/.test(data.type)) return;
      url = URL.createObjectURL(data); setPreview({ url, type: data.type });
    }).catch(() => {});
    return () => { controller.abort(); if (url) URL.revokeObjectURL(url); };
  }, [api, open, current]);
  const download = async () => {
    if (!current) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.get(current.url || `/files/${encodeURIComponent(current.fileId)}/download`, {
        params: current.url ? undefined : { orig: 1 }, responseType: "blob",
      });
      const url = URL.createObjectURL(data), link = document.createElement("a");
      link.href = url; link.download = current.filename || "document"; document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { setError(zh ? "下载失败，请重试。" : "Download failed. Retry."); } finally { setBusy(false); }
  };
  return <>
    <Button type="button" variant="outline" size={compact ? "sm" : "default"} className="min-h-11 gap-1.5 whitespace-normal" onClick={() => setOpen(true)} data-testid={`document-scan-versions-${fileId || "private"}`}><Files className="h-4 w-4" aria-hidden="true" />{zh ? "原件 / 扫描件" : "Originals / scan"}</Button>
    <Dialog open={open} onOpenChange={setOpen}><DialogContent className="max-w-5xl h-[85dvh] flex flex-col bg-background z-[11000]" overlayClassName="z-[10999]" closeLabel={zh ? "关闭版本预览" : "Close versions"}>
      <DialogHeader><DialogTitle>{zh ? "凭证文件版本" : "Document versions"}</DialogTitle><DialogDescription>{zh ? "原始文件保持上传时的内容，扫描件单独保存。" : "Original files are unchanged. The scanned copy is stored separately."}</DialogDescription></DialogHeader>
      {error && <p role="alert" className="text-sm text-destructive">{String(error)}</p>}
      {busy && !items.length && <p role="status" className="text-sm text-muted-foreground">{zh ? "正在加载…" : "Loading…"}</p>}
      <div className="flex flex-wrap items-center gap-2">{items.map((item, index) => <Button key={item.fileId || item.url || index} type="button" variant={index === selected ? "default" : "outline"} className="max-w-full min-h-11 whitespace-normal" onClick={() => setSelected(index)} aria-pressed={index === selected}>
        <span className="max-w-64 truncate">{item.scanned ? (zh ? "扫描件" : "Scanned") : `${zh ? "原件" : "Original"} ${index + (items[0]?.scanned ? 0 : 1)}`} · {item.filename}</span>
      </Button>)}</div>
      <div className="min-h-0 flex-1 overflow-auto rounded-xl border bg-muted/30 p-2">
        {preview.url ? (preview.type === "application/pdf" ? <iframe title={current?.filename || "PDF"} src={preview.url} className="w-full h-full min-h-64 border-0" /> : <img alt={current?.filename || ""} src={preview.url} className="mx-auto max-w-full max-h-full object-contain" />)
          : <div className="flex h-full min-h-48 flex-col items-center justify-center gap-3 text-muted-foreground"><FileText aria-hidden="true" /><p className="text-sm">{zh ? "可下载原文件查看完整内容。" : "Download the original to view its full contents."}</p></div>}
      </div>
      <div className="flex flex-wrap justify-between items-center gap-2"><span className="text-sm text-muted-foreground">{current?.size ? scanSize(current.size) : ""}</span><Button type="button" onClick={download} disabled={busy || !current} className="min-h-11">{busy ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />}{zh ? "下载当前文件" : "Download selected"}</Button></div>
    </DialogContent></Dialog>
  </>;
}
